import { useCallback, useState } from 'react'
import { DownloadForm } from '../components/DownloadForm'
import { BulkDownloadForm } from '../components/BulkDownloadForm'
import { ModeToggle } from '../components/ModeToggle'
import { ProgressBar } from '../components/ProgressBar'
import { StatusMessage } from '../components/StatusMessage'
import { VideoPreview } from '../components/VideoPreview'
import { ActionButtons } from '../components/ActionButtons'
import { ArchiveStatus } from '../components/ArchiveStatus'
import { useVideoDownload } from '../hooks/useVideoDownload'
import { useBulkDownload } from '../hooks/useBulkDownload'
import type { Quality, DownloadMode } from '../types'

/**
 * Moved verbatim from the former `App.tsx` when the shell landed (SHUT-30) — only the outer wrapper
 * changed, because `AppShell` now owns the background and the viewport height. The section order and
 * grouping inside the card are deliberately untouched here; SHUT-31 is what brings them into line
 * with `SingleDownloadView.swift`.
 */
export function DownloadRoute() {
  const [mode, setMode] = useState<DownloadMode>('single')

  const singleDownload = useVideoDownload()
  const bulkDownload = useBulkDownload()

  const handleSingleSubmit = useCallback(
    (url: string, quality: Quality) => {
      singleDownload.download(url, quality)
    },
    [singleDownload]
  )

  const handleBulkSubmit = useCallback(
    (urls: string[], quality: Quality) => {
      bulkDownload.download(urls, quality)
    },
    [bulkDownload]
  )

  const handleModeChange = useCallback((newMode: DownloadMode) => {
    setMode(newMode)
    singleDownload.reset()
    bulkDownload.reset()
  }, [singleDownload, bulkDownload])

  const isLoading = singleDownload.state === 'fetching' ||
    singleDownload.state === 'downloading' ||
    bulkDownload.state === 'downloading' ||
    bulkDownload.state === 'preparing'

  return (
    <div className="p-5 flex flex-col items-center justify-center min-h-[calc(100vh-3.5rem)]">
      <div className="w-full max-w-[400px] bg-white/10 backdrop-blur-md rounded-2xl p-7 shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
        <h1 className="text-white text-2xl font-bold text-center mb-1">
          Video Downloader
        </h1>
        <ArchiveStatus />

        <ModeToggle
          mode={mode}
          onChange={handleModeChange}
          disabled={isLoading}
        />

        {mode === 'single' ? (
          <>
            <DownloadForm
              onSubmit={handleSingleSubmit}
              isLoading={singleDownload.state === 'fetching' || singleDownload.state === 'downloading'}
            />

            <ProgressBar
              progress={singleDownload.progress}
              visible={singleDownload.state === 'downloading' && singleDownload.progress > 0}
            />

            <StatusMessage
              state={singleDownload.state}
              phase={singleDownload.phase}
              progress={singleDownload.progress}
              errorMessage={singleDownload.errorMessage}
              canShare={singleDownload.canShare}
            />

            <VideoPreview src={singleDownload.result?.objectUrl ?? null} />

            <ActionButtons
              result={singleDownload.result}
              canShare={singleDownload.canShare}
              sourceUrl={singleDownload.sourceUrl}
              onShareToPhotos={singleDownload.shareToPhotos}
            />
          </>
        ) : (
          <>
            <BulkDownloadForm
              onSubmit={handleBulkSubmit}
              isLoading={bulkDownload.state === 'downloading'}
            />

            <ProgressBar
              progress={bulkDownload.progress}
              visible={bulkDownload.state === 'downloading'}
            />

            {bulkDownload.state === 'preparing' && (
              <div className="mt-5 px-4 py-4 rounded-xl text-center bg-twitter-blue/20 text-twitter-blue">
                <span className="inline-block w-4 h-4 border-2 border-transparent border-t-current rounded-full animate-spin mr-2 align-middle" />
                Fetching {bulkDownload.urlCount} videos...
              </div>
            )}

            {bulkDownload.state === 'downloading' && (
              <div className="mt-5 px-4 py-4 rounded-xl text-center bg-twitter-blue/20 text-twitter-blue">
                <span className="inline-block w-4 h-4 border-2 border-transparent border-t-current rounded-full animate-spin mr-2 align-middle" />
                Creating ZIP file...
              </div>
            )}

            {bulkDownload.state === 'complete' && bulkDownload.result && (
              <div className="mt-5 space-y-3">
                <div className="px-4 py-4 rounded-xl text-center bg-success-green/20 text-success-green">
                  Download complete! Your ZIP file is ready.
                </div>
                <button
                  onClick={bulkDownload.saveZip}
                  className="w-full py-4 text-lg font-semibold bg-ios-blue text-white rounded-xl transition-colors hover:bg-ios-blue-dark"
                >
                  Save ZIP File
                </button>
              </div>
            )}

            {bulkDownload.state === 'error' && (
              <div className="mt-5 px-4 py-4 rounded-xl text-center bg-error-red/20 text-error-red">
                {bulkDownload.errorMessage || 'An error occurred'}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
